import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, ExplorerWordmark, Flex, Spin, Typography, theme } from "@murm/ant";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { consumeSessionExpiredFlag } from "../../lib/sessionExpiry";
import styles from "./Login.module.css";

const { Text } = Typography;

const Login = () => {
  const { loginWithRedirect, isAuthenticated, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startedRedirect = useRef(false);
  const { token } = theme.useToken();
  // The ?session=expired query param is kept as a fallback (e.g. a direct link), but
  // Auth0's returnTo can only target the bare origin (see lib/sessionExpiry.js), so the
  // real signal from App.js's session-recovery logout arrives via sessionStorage.
  const [sessionExpiredFlag] = useState(consumeSessionExpiredFlag);
  const sessionExpired =
    searchParams.get("session") === "expired" || sessionExpiredFlag;
  const authError = Boolean(error) || searchParams.get("authError") === "1";

  useEffect(() => {
    if (error) return;
    if (isLoading) return;
    if (isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    if (startedRedirect.current) return;
    startedRedirect.current = true;
    loginWithRedirect({
      appState: { returnTo: "/" },
    });
  }, [error, loginWithRedirect, isAuthenticated, isLoading, navigate]);

  const retryLogin = () => {
    startedRedirect.current = true;
    loginWithRedirect({
      appState: { returnTo: "/" },
    });
  };

  if (authError) {
    return (
      <Flex
        align="center"
        justify="center"
        className={styles.errorScreen}
      >
        <Alert
          type="error"
          showIcon
          message="We couldn't sign you in"
          description={
            <Flex vertical gap={token.marginSM}>
              <p className={styles.errorText}>
                {error?.message || "Authentication failed. Please try signing in again."}
              </p>
              <Button type="primary" onClick={retryLogin} className={styles.retry}>
                Try again
              </Button>
            </Flex>
          }
          className={styles.errorAlert}
        />
      </Flex>
    );
  }

  return (
    <Flex className={styles.screen}>
      {/* Left panel — branding */}
      <Flex
        vertical
        justify="space-between"
        className={styles.brandPanel}
      >
        {/* The approved Explorer logo (Explorer Figma, Logo); replaces the old
            "organizer / introducing explorer" lockup. */}
        <div className={styles.logo}>
          <ExplorerWordmark size="md" />
        </div>
        <Flex vertical gap={token.marginMD}>
          {sessionExpired ? (
            <Alert
              type="warning"
              showIcon
              message="Session expired"
              description="We couldn’t refresh your sign-in. You’ll be redirected to sign in again."
              className={styles.expired}
            />
          ) : null}
          <Spin size="large" />
          <Text type="secondary" className={styles.status}>
            Redirecting to login…
          </Text>
        </Flex>
        <div />
      </Flex>

      {/* Right panel — rocketship illustration */}
      <Flex
        align="center"
        justify="center"
        className={styles.illustrationPanel}
      >
        <img
          src="/rocketship.svg"
          alt=""
          aria-hidden="true"
          className={styles.illustration}
        />
      </Flex>
    </Flex>
  );
};

export default Login;
